class AudioPlayer {
  constructor(playerElement) {
    if (!window.audioTracks) {
      console.warn('No audio tracks defined');
      return;
    }
    this.player = playerElement;
    if (!this.player) {
      console.warn('No audio player element provided');
      return;
    }
    this.baseURL = window.audioBaseURL || '';
    this.tracks = window.audioTracks.sort((a, b) => a.order - b.order);
    this.currentTrack = null;
    this.seeking = false;
    this.resumeAfterSeeking = false;
    this.tracklist = [];
    this._buildPlayer();
    this._init();
  }

  _buildPlayer() {
    const tracksMarkup = this.tracks
      .map((track, index) => this._trackTemplate(track, index))
      .filter(Boolean)
      .join('');
    this.player.innerHTML = tracksMarkup;
  }

  _trackTemplate(track, index) {
        // console.log(`Track URL:`, track);
    if (typeof track === 'string') {
      try {
        track = JSON.parse(track);
      } catch (e) {
        console.warn(`Failed to parse track ${index}:`, track);
        // return '';
      }
    }
    // if (!track.url) {
    //   console.warn(`Track ${track.name || index} has no URL, skipping.`);
    //   return '';
    // }
    if(track.url.includes('http://audio.turntablelab.com/')){
      track.url = track.url.replace('http://audio.turntablelab.com/', '');
    }else {
      track.url = track.url.replace('https://www.turntablelab.com/', '');
    }
    const number = index + 1;
    const fullUrl = this.baseURL + track.url;
    return `
      <li
        class="audio-track${track.url ? '' : ' audio-track--no-preview'} ${!track.url ? 'no-audio-track-url' : ''}"
        data-audio-url="${fullUrl}"
        data-audio-track
      >
        <span class="audio-track__number">${number >= 10 ? number : `0${number}`}</span>
        ${track.url ? `
          <button class="audio-track__button" type="button" data-audio-button>
            <span class="play-button music-player-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22px" height="22px" fill="#fff">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </span>
            <span class="pause-button music-player-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22px" height="22px" fill="#fff">
                <path d="M6 19h4V5H6zm8-14v14h4V5z"/>
              </svg>
            </span>
          </button>
        ` : ''}
        <div class="audio-track__details">
          <p class="audio-track__title">${track.name || 'Untitled'}</p>
          <div class="audio-track__seek" data-audio-seek>
            <span class="audio-track__timestamp" data-audio-current-time>0:00</span>
            <div class="audio-track__progress-wrap" data-audio-progress-wrap>
              <span class="audio-track__progress-bar" data-audio-progress></span>
            </div>
            <span class="audio-track__timestamp" data-audio-length>0:00</span>
          </div>
        </div>
      </li>
    `;
  }

  _init() {
    const trackElements = this.player.querySelectorAll('[data-audio-track]');
    trackElements.forEach((el, index) => {
      this._initializeHowl(el, index);
      const track = this.tracklist[index];
      const playButton = el.querySelector('[data-audio-button]');
      if (playButton) {
        playButton.addEventListener('click', this._handlePlayButtonClick.bind(this, track, el, index));
      }
      const progressWrap = el.querySelector('[data-audio-progress-wrap]');
      if (progressWrap) {
        progressWrap.addEventListener('mousedown', this._setSeek.bind(this, true));
        progressWrap.addEventListener('mouseup', this._seek.bind(this, progressWrap, track));
      }
      el.addEventListener('keydown', this._handleKeyPress.bind(this, track, index));
    });
  }

  _initializeHowl(el, index) {
    const howl = new Howl({
      src: [el.dataset.audioUrl],
      html5: true,
      preload: true
    });
    this.tracklist.push(howl);
    howl.on('load', () => {
      const trackLength = el.querySelector('[data-audio-length]');
      trackLength.innerHTML = this._formatTime(Math.round(howl.duration()));
      el.classList.remove('audio-track--loading');
      el.classList.add('audio-track--loaded');
    });
    howl.on('play', () => {
      el.classList.remove('audio-track--paused');
      el.classList.add('audio-track--playing');
      requestAnimationFrame(this._progressUpdater.bind(this, howl, el, index));
    });
    howl.on('pause', () => {
      el.classList.remove('audio-track--playing');
      el.classList.add('audio-track--paused');
    });
    howl.on('seek', () => {
      requestAnimationFrame(this._progressUpdater.bind(this, howl, el, index));
    });
    howl.on('stop', () => {
      el.classList.remove('audio-track--playing');
      el.classList.remove('audio-track--paused');
    });
    howl.on('end', () => {
      el.classList.remove('audio-track--playing');
    });
  }

  _formatTime(secs) {
    const minutes = Math.floor(secs / 60) || 0;
    const seconds = secs - minutes * 60 || 0;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  _progressUpdater(track, el, index) {
    const seek = track.seek() || 0;
    const currentTime = el.querySelector('[data-audio-current-time]');
    currentTime.innerHTML = this._formatTime(Math.round(seek));
    const progressBar = el.querySelector('[data-audio-progress]');
    const currentProgress = (seek / track.duration()) * 100 || 0;
    progressBar.style.width = `${currentProgress}%`;
    if (track.playing()) {
      requestAnimationFrame(this._progressUpdater.bind(this, track, el, index));
    }
  }

  _handleKeyPress(track, index, event) {
    if (!['Space', 'ArrowLeft', 'ArrowRight'].includes(event.code)) return;
    const currentPos = track.seek();
    if (event.code === 'Space') {
      if (this.currentTrack !== null && index !== this.currentTrack) {
        this.tracklist[this.currentTrack].stop();
      }
    }
    if (event.code === 'ArrowLeft') {
      event.preventDefault();
      track.seek(currentPos > 5 ? currentPos - 5 : 0);
      return;
    }
    if (event.code === 'ArrowRight') {
      event.preventDefault();
      if (currentPos < track.duration() - 5) {
        track.seek(currentPos + 5);
      } else {
        track.stop();
      }
    }
  }

  _handlePlayButtonClick(track, el, index) {
    if (!el.classList.contains('audio-track--loaded')) {
      el.classList.add('audio-track--loading');
    }
    if (index === this.currentTrack && track.playing()) {
      track.pause();
      return;
    }
    if (this.currentTrack !== null && index !== this.currentTrack) {
      this.tracklist[this.currentTrack].stop();
    }
    track.play();
    this.currentTrack = index;
  }

  _setSeek(val) {
    this.seeking = val;
  }

  _seek(progressWrap, track, event) {
    if (event.type !== 'mouseup') return;
    if (!track._duration) {
      console.warn('Track duration not available, cannot seek');
      return;
    }
    if (track.playing()) {
      track.pause();
      this.resumeAfterSeeking = true;
    }
    const rect = progressWrap.getBoundingClientRect();
    const clickPos = event.pageX - rect.left;
    const seekPos = Math.max(0, Math.min((clickPos / rect.width) * track.duration(), track.duration()));
    console.log(`Seeking to ${seekPos} seconds (clickPos: ${clickPos}, width: ${rect.width}, duration: ${track.duration()})`);
    track.seek(seekPos);
    if (this.resumeAfterSeeking) {
      track.play();
    }
    this.resumeAfterSeeking = false;
    this.seeking = false;
  }

  unload() {
    const trackElements = this.player.querySelectorAll('[data-audio-track]');
    trackElements.forEach((el, index) => {
      const track = this.tracklist[index];
      const playButton = el.querySelector('[data-audio-button]');
      if (playButton) {
        playButton.removeEventListener('click', this._handlePlayButtonClick.bind(this, track, el, index));
      }
      const progressWrap = el.querySelector('[data-audio-progress-wrap]');
      if (progressWrap) {
        progressWrap.removeEventListener('mousedown', this._setSeek.bind(this, true));
        progressWrap.removeEventListener('mouseup', this._seek.bind(this, progressWrap, track));
      }
      el.removeEventListener('keydown', this._handleKeyPress.bind(this, track, index));
      track.unload();
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const playerElements = document.querySelectorAll('[data-audio-player]');
  playerElements.forEach((playerElement) => {
    new AudioPlayer(playerElement);
  });
});